import { PrivilegeType } from '../enums/privilege-type.enum';
import { Injectable } from '@angular/core';
import { Privilege } from '../models/permission.model';
import { CacheService } from './cache.service';
import { CacheKey } from '../constants/cachekey.constants';
import { Portals } from 'src/app/common/enums/portal.enum';
import { StorePortal } from '../interfaces/store-config.interface';
import { StorePortalService } from './store-portal.service';
import { StorePortalFeature } from '../interfaces/privilege.interface';
import { StorePortalFeatureService } from './store-portal-feature.service';

@Injectable()
export class AuthService {
    private _privileges: Privilege[];
    private _storePortals: StorePortal[];
    private _privilegesLoaded: boolean = false;
    private _storePortalsLoaded: boolean = false;
    constructor(private _cacheService: CacheService,
        private _storePortalService: StorePortalService,
        private _storePortalFeatureService: StorePortalFeatureService) {
    }

    /**
     * Initialize the store features
     */
    async init(): Promise<any> {
        return this.fillInPrivileges();
    }

    /**
     * Validate whether the feature is enabled or not
     * @param privilegeNames 
     * @param privilegeType 
     */
    async hasPrivilege(privilegeNames: string[], privilegeType?: PrivilegeType[]): Promise<boolean> {
        const promise = new Promise<boolean>((resolve, reject) => {
            this.hasSuperUserAccess()
                .then(async (res) => {
                    if (res) {
                        resolve(res);
                    }
                    else {
                        if (privilegeNames === null || privilegeNames === undefined
                            || privilegeNames.length === 0) {
                            resolve(true);
                        }
                        await this.validatePrivileges(privilegeNames)
                            .then((res) => {
                                resolve(res);
                            });
                    }
                }).
                catch(err => {
                    resolve(true);
                });
        });
        return promise;
    }

    /**
     * Validate Privileges
     * @param privilegeNames 
     */
    private async validatePrivileges(privilegeNames: string[]): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            let hasPrivilege = this.hasRequiredPrivilege(privilegeNames);
            resolve(hasPrivilege);
        });
    }

    /**
     * validate privilege existance
     * @param privilegeNames 
     */
    private hasRequiredPrivilege(privilegeNames: string[]) {
        if (this._privileges) {
            for (const privilege of privilegeNames) {
                let exists = (this._privileges.filter(x => x.code.toLowerCase()
                    == privilege.toLowerCase()).length > 0) ? true : false;
                if (exists) {
                    return exists;
                }
            }
            return false;
        } else {
            return true;
        }
    }

    /**
     * Initialize the privileges
     */
    private async initializePrivileges(): Promise<boolean> {
        return new Promise<boolean>(async (resolve, reject) => {
            if ((this._privileges === undefined || this._privileges === null)) {
                let privileges: StorePortalFeature[] = this._cacheService.get(CacheKey.Store_Portal_Features);
                if ((privileges === undefined || privileges === null) && !this._privilegesLoaded) {
                    const store = this._cacheService.get(CacheKey.StoreConfiguration);
                    if (store && store.id) {
                    await this._storePortalFeatureService.getStorePortalFeatures(store.id, Portals.Corporate)
                        .then(res => {
                            this._privileges = res;
                            this._privilegesLoaded = true;
                        });
                    resolve(true);
                    } else {
                        reject(false);
                    }
                }
                else {
                    this._privileges = privileges;
                    this._privilegesLoaded = true;
                    resolve(true);
                }
            }
            else {
                resolve(true);
            }
        });

    }

    /**
     * Check and load privileges
     */
    private async fillInPrivileges() {
        await this.hasSuperUserAccess()
            .then(res => {
                if (!res) {
                    return this.initializePrivileges();
                }
            })
            .catch(err => {
                return this.initializePrivileges();
            });
    }

    /**
     * Check the Portal mapping for a given portal id in store
     * @param portalId 
     */
    async hasAccessToPortal(portalId: number): Promise<boolean> {
        let hasAccess = true;
        if ((this._storePortals === undefined || this._storePortals === null) && !this._storePortalsLoaded) {
            await this.loadStorePortals();
            this._storePortalsLoaded = true;
        }
        if (this._storePortals !== undefined
            && this._storePortals !== null && this._storePortals.length > 0) {
            hasAccess = (this._storePortals.findIndex(m => m.id === Portals.All) == -1 ? false : true);
            if (!hasAccess && portalId > 0) {
                hasAccess = this._storePortals.findIndex(m => m.id === portalId) === -1 ? false : true;
            }
        }
        return hasAccess;
    }
    /**
     * Validate the store access levels
     */
    private hasSuperUserAccess = (): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            this.hasAccessToPortal(Portals.All)
                .then((res) => {
                    resolve(res);
                })
                .catch((err) => {
                    reject(false);
                });
        });
    }
    /**
     * Load Store Portals Information
     */
    private loadStorePortals = async () => {
        const store = this._cacheService.get(CacheKey.StoreConfiguration);
        if (store !== undefined && store !== null) {
            if (store.portals !== undefined
                && store.portals !== null && store.portals.length > 0) {
                this._storePortals = store.portals;
            }
            else {
                await this._storePortalService.getStorePortals(store.id).then(res => {
                    this._storePortals = res;
                });
            }
        }
    }
}